import JSZip from 'jszip';
import { useCallback, useState } from 'react';
import { MAX_BULK_FILES_WARNING } from './constants';
import type { Asset, FilesApi } from './types';

interface UseFileDownloadOptions {
    patientId: string | undefined;
    api: FilesApi;
    /** Optional base for the selected-files zip name, e.g. the patient name. */
    zipBaseName?: string;
}

interface UseFileDownloadReturn {
    downloadingCategory: string | null;
    downloadError: string | null;
    clearDownloadError: () => void;
    getSignedUrl: (asset: Asset) => Promise<string>;
    getAssetsWithSignedUrls: (assets: Asset[]) => Promise<Asset[]>;
    downloadSingleFile: (asset: Asset) => Promise<void>;
    downloadAssetsAsZip: (assets: Asset[], zipName: string) => Promise<void>;
    handleDownloadCategory: (category: { label: string; assets: Asset[] }) => Promise<void>;
    handleDownloadSelected: (
        selectedAssets: Set<string>,
        allAssets: Record<string, Asset>,
        onComplete: () => void
    ) => Promise<void>;
    shouldWarnBeforeDownload: (count: number) => boolean;
}

// Deduplicates filenames by appending _1, _2, etc. for collisions
const deduplicateFilename = (name: string, usedNames: Set<string>): string => {
    if (!usedNames.has(name)) {
        usedNames.add(name);
        return name;
    }

    const dotIdx = name.lastIndexOf('.');
    const base = dotIdx > 0 ? name.slice(0, dotIdx) : name;
    const ext = dotIdx > 0 ? name.slice(dotIdx) : '';

    let counter = 1;
    let candidate = `${base}_${counter}${ext}`;
    while (usedNames.has(candidate)) {
        counter++;
        candidate = `${base}_${counter}${ext}`;
    }
    usedNames.add(candidate);
    return candidate;
};

const sanitizeForFilename = (value: string): string =>
    value
        .trim()
        .replaceAll(/[/\\:*?"<>|]/g, '')
        .replaceAll(/\s+/g, '_');

const selectedZipName = (zipBaseName: string | undefined, count: number): string => {
    const base = zipBaseName ? sanitizeForFilename(zipBaseName) : '';
    if (!base) return `selected_files_${count}.zip`;
    const date = new Date().toISOString().slice(0, 10);
    return `${base}_${date}.zip`;
};

export const useFileDownload = ({
    patientId,
    api,
    zipBaseName
}: UseFileDownloadOptions): UseFileDownloadReturn => {
    const [downloadingCategory, setDownloadingCategory] = useState<string | null>(null);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const clearDownloadError = useCallback(() => setDownloadError(null), []);

    const getSignedUrl = useCallback(
        async (asset: Asset): Promise<string> => {
            if (asset.source === 'gcs' && asset.patientFileId && patientId) {
                return api.getDownloadUrl(patientId, asset.patientFileId);
            }
            return asset.publicUrl;
        },
        [patientId, api]
    );

    const getAssetsWithSignedUrls = useCallback(
        async (targetAssets: Asset[]): Promise<Asset[]> =>
            Promise.all(
                targetAssets.map(async (asset) => {
                    if (asset.source === 'gcs' && !asset.publicUrl && asset.patientFileId) {
                        try {
                            const downloadUrl = await getSignedUrl(asset);
                            return { ...asset, publicUrl: downloadUrl };
                        } catch {
                            return asset;
                        }
                    }
                    return asset;
                })
            ),
        [getSignedUrl]
    );

    const downloadSingleFile = useCallback(
        async (asset: Asset): Promise<void> => {
            if (asset.source === 'gcs') {
                const downloadUrl = await getSignedUrl(asset);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = asset.name;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                // Monday.com signed S3 URLs already carry response-content-disposition=attachment,
                // so a direct <a download> avoids CORS preflight and bypasses the backend proxy
                // (which 500s on large files due to GAE timeout/size limits).
                const link = document.createElement('a');
                link.href = asset.publicUrl;
                link.download = asset.name;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        },
        [getSignedUrl]
    );

    const downloadAssetsAsZip = useCallback(
        async (assets: Asset[], zipName: string): Promise<void> => {
            const assetsWithUrls = await getAssetsWithSignedUrls(assets);
            const zip = new JSZip();
            const usedNames = new Set<string>();
            let failedCount = 0;

            const fetchBlob = async (asset: Asset): Promise<{ asset: Asset; blob: Blob }> => {
                const blob =
                    asset.source === 'gcs'
                        ? await fetch(asset.publicUrl).then((r) => r.blob())
                        : await api.fetchViaProxy(asset.publicUrl, asset.name);
                return { asset, blob };
            };

            for (let i = 0; i < assetsWithUrls.length; i += 5) {
                const batch = assetsWithUrls.slice(i, i + 5);
                const results = await Promise.allSettled(batch.map(fetchBlob));
                for (const result of results) {
                    if (result.status === 'fulfilled') {
                        const safeName = deduplicateFilename(result.value.asset.name, usedNames);
                        zip.file(safeName, result.value.blob);
                    } else {
                        failedCount++;
                    }
                }
            }

            if (Object.keys(zip.files).length === 0) {
                setDownloadError('All file downloads failed. Please try again.');
                return;
            }

            if (failedCount > 0) {
                setDownloadError(
                    `${failedCount} file(s) could not be downloaded and were skipped.`
                );
            }

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const zipUrl = URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = zipUrl;
            link.download = zipName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(zipUrl);
        },
        [getAssetsWithSignedUrls, api]
    );

    const handleDownloadCategory = useCallback(
        async (category: { label: string; assets: Asset[] }) => {
            if (category.assets.length === 0) return;

            setDownloadingCategory(category.label);
            setDownloadError(null);
            try {
                if (category.assets.length === 1) {
                    await downloadSingleFile(category.assets[0]);
                } else {
                    const zipName = `${category.label.replaceAll(/\s+/g, '_')}_files.zip`;
                    await downloadAssetsAsZip(category.assets, zipName);
                }
            } catch {
                setDownloadError('Failed to download files. Please try again.');
            } finally {
                setDownloadingCategory(null);
            }
        },
        [downloadSingleFile, downloadAssetsAsZip]
    );

    const handleDownloadSelected = useCallback(
        async (
            selectedAssets: Set<string>,
            allAssets: Record<string, Asset>,
            onComplete: () => void
        ) => {
            if (selectedAssets.size === 0) return;

            const selectedList = Object.values(allAssets).filter((a) =>
                selectedAssets.has(String(a.assetId))
            );
            if (selectedList.length === 0) return;

            setDownloadError(null);
            try {
                if (selectedList.length === 1) {
                    await downloadSingleFile(selectedList[0]);
                } else {
                    await downloadAssetsAsZip(
                        selectedList,
                        selectedZipName(zipBaseName, selectedList.length)
                    );
                }
            } catch {
                setDownloadError('Failed to download selected files. Please try again.');
            } finally {
                onComplete();
            }
        },
        [downloadSingleFile, downloadAssetsAsZip, zipBaseName]
    );

    const shouldWarnBeforeDownload = useCallback(
        (count: number) => count > MAX_BULK_FILES_WARNING,
        []
    );

    return {
        downloadingCategory,
        downloadError,
        clearDownloadError,
        getSignedUrl,
        getAssetsWithSignedUrls,
        downloadSingleFile,
        downloadAssetsAsZip,
        handleDownloadCategory,
        handleDownloadSelected,
        shouldWarnBeforeDownload
    };
};
